<template>
  <div class="flex flex-col divide-y divide-gray-200">
    <div
      v-for="(alternative, index) in localAlternatives"
      :key="alternative._tempId"
      class="group cursor-pointer"
      @click.stop="toggleExpand(index)"
    >
      <div
        class="flex items-start gap-2 px-4 py-4 bg-white w-full hover:bg-gray-50 transition-colors"
      >
        <div class="flex flex-col flex-1 min-w-0">
          <span class="text-base font-medium text-gray-900">{{
            useAlternativeName(alternative, index).value
          }}</span>
          <span v-if="alternative.description" class="text-sm text-gray-500 mt-1 font-normal">{{
            alternative.description
          }}</span>
        </div>
        <button
          type="button"
          class="p-1 text-red-600 disabled:opacity-50 shrink-0 self-start transition-opacity"
          :disabled="disabled"
          @click.stop="removeAlternative(index)"
        >
          <XMarkIcon class="w-4 h-4" />
        </button>
      </div>

      <div v-if="expandedIndex === index" class="px-4 pb-4" @click.stop>
        <div class="flex flex-col gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-semibold text-gray-900">Name</label>
            <Combobox
              v-model="alternative.name"
              allow-create
              :items="review.medicalAlternativeNames.value"
              :display-fn="(i) => i"
              placeholder="Alternative name"
              :disabled="disabled"
              :create-message="(v) => v"
              @update:model-value="syncToServer"
              @create="
                (v) => {
                  alternative.name = v;
                  syncToServer();
                }
              "
              @enter="syncToServer"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm font-semibold text-gray-900">Description</label>
            <TextInput
              v-model="alternative.description"
              placeholder="Description (optional)"
              :disabled="disabled"
              multiline
              input-classes="w-full"
              @blur="syncToServer"
              @enter="syncToServer"
            />
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-gray-900">Indications</label>
            <div class="flex flex-wrap gap-2 items-center">
              <div
                v-for="(indication, indicationIndex) in alternative.indications"
                :key="'indication-' + indicationIndex"
                class="flex items-center text-sm gap-1 px-4 py-2.5 rounded-full text-green-700 bg-green-100 font-medium"
              >
                <span
                  v-tooltip="indication.length > 35 ? indication : ''"
                  class="truncate max-w-[252px]"
                  >{{ indication }}</span
                >
                <button
                  v-if="!review.isReviewReadonly.value"
                  :disabled="disabled"
                  @click="removeFromArray(alternative.indications, indicationIndex)"
                >
                  <XMarkIcon class="w-4 h-4" />
                </button>
              </div>
              <Combobox
                v-if="!review.isReviewReadonly.value"
                :model-value="''"
                allow-create
                :items="review.medicalAlternativeIndications.value"
                :display-fn="(i) => i"
                placeholder="Add indication"
                :disabled="disabled"
                :create-message="(v) => v"
                input-classes="!flex !items-center !gap-1 !text-sm !border-2 !py-1.5! !px-3 !rounded-full !text-gray-700 !border-gray-200 !border-dashed !font-medium"
                @update:model-value="(v) => addToArray(alternative.indications, v!)"
                @create="(v) => addToArray(alternative.indications, v)"
              />
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-gray-900">Contraindications</label>
            <div class="flex flex-wrap gap-2 items-center">
              <div
                v-for="(contraindication, contraIndex) in alternative.contraindications"
                :key="'contra-' + contraIndex"
                class="flex items-center text-sm gap-1 px-4 py-2.5 rounded-full text-red-700 bg-red-100 font-medium"
              >
                <span
                  v-tooltip="contraindication.length > 35 ? contraindication : ''"
                  class="truncate max-w-[252px]"
                  >{{ contraindication }}</span
                >
                <button
                  v-if="!review.isReviewReadonly.value"
                  :disabled="disabled"
                  @click="removeFromArray(alternative.contraindications, contraIndex)"
                >
                  <XMarkIcon class="w-4 h-4" />
                </button>
              </div>
              <Combobox
                v-if="!review.isReviewReadonly.value"
                :model-value="''"
                allow-create
                :items="review.medicalAlternativeContraindications.value"
                :display-fn="(i) => i"
                placeholder="Add contraindication"
                :disabled="disabled"
                :create-message="(v) => v"
                input-classes="!flex !items-center !gap-1 !text-sm !border-2 !py-1.5! !px-3 !rounded-full !text-gray-700 !border-gray-200 !border-dashed !font-medium"
                @update:model-value="(v) => addToArray(alternative.contraindications, v!)"
                @create="(v) => addToArray(alternative.contraindications, v)"
              />
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-gray-900">Target Population</label>
            <div class="flex flex-wrap gap-2 items-center">
              <div
                v-for="(target, targetIndex) in alternative.targetPopulation"
                :key="'target-' + targetIndex"
                class="flex items-center text-sm gap-1 px-4 py-2.5 rounded-full text-green-700 bg-green-100 font-medium"
              >
                <span v-tooltip="target.length > 35 ? target : ''" class="truncate max-w-[252px]">{{
                  target
                }}</span>
                <button
                  v-if="!review.isReviewReadonly.value"
                  :disabled="disabled"
                  @click="removeFromArray(alternative.targetPopulation, targetIndex)"
                >
                  <XMarkIcon class="w-4 h-4" />
                </button>
              </div>
              <Combobox
                v-if="!review.isReviewReadonly.value"
                :model-value="''"
                allow-create
                :items="review.medicalAlternativeTargetPopulation.value"
                :display-fn="(i) => i"
                placeholder="Add target population"
                :disabled="disabled"
                :create-message="(v) => v"
                input-classes="!flex !items-center !gap-1 !text-sm !border-2 !py-1.5! !px-3 !rounded-full !text-gray-700 !border-gray-200 !border-dashed !font-medium"
                @update:model-value="(v) => addToArray(alternative.targetPopulation, v!)"
                @create="(v) => addToArray(alternative.targetPopulation, v)"
              />
            </div>
          </div>

          <div class="mt-2 space-y-5">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-semibold text-gray-900">
                Position in line of therapy
              </label>

              <div class="flex items-center gap-6">
                <label
                  v-for="option in positionInTherapyOptions"
                  :key="option.value"
                  class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="radio"
                    :name="`positionInTherapy-${alternative._tempId}`"
                    :value="option.value"
                    v-model="alternative.assessment.positionInTherapy"
                    class="w-4 h-4 border-gray-300 accent-blue-600 focus:ring-blue-500"
                    :disabled="disabled"
                    @change="syncToServer"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-semibold text-gray-900">
                Benchmark authority<span
                  class="cursor-help font-normal text-gray-400"
                  title="Gold-standard: Widely accepted benchmark with the strongest evidence and clinical consensus. Indicates the best-established option, not novelty."
                >
                  ⓘ
                </span>
              </label>
              <div class="flex items-center gap-6">
                <label
                  v-for="option in benchmarkAuthorityOptions"
                  :key="option.value"
                  class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="radio"
                    :name="`benchmarkAuthority-${alternative._tempId}`"
                    :value="option.value"
                    v-model="alternative.assessment.benchmarkAuthority"
                    class="w-4 h-4 border-gray-300 accent-blue-600 focus:ring-blue-500"
                    :disabled="disabled"
                    @change="syncToServer"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-semibold text-gray-900"> Mode of use </label>

              <div class="flex items-center gap-6">
                <label
                  v-for="option in modeOfUseOptions"
                  :key="option.value"
                  class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="radio"
                    :name="`modeOfUse-${alternative._tempId}`"
                    :value="option.value"
                    v-model="alternative.assessment.modeOfUse"
                    class="w-4 h-4 border-gray-300 accent-blue-600 focus:ring-blue-500"
                    :disabled="disabled"
                    @change="syncToServer"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-semibold text-gray-900">
                Innovation status
                <span
                  class="cursor-help font-normal text-gray-400"
                  title="First-in-class: Introduces a new mechanism or technology not previously available. Indicates novelty, not that it is the best or most established option."
                >
                  ⓘ
                </span>
              </label>

              <div class="flex items-center gap-6">
                <label
                  v-for="option in innovationStatusOptions"
                  :key="option.value"
                  class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="radio"
                    :name="`innovationStatus-${alternative._tempId}`"
                    :value="option.value"
                    v-model="alternative.assessment.innovationStatus"
                    class="w-4 h-4 border-gray-300 accent-blue-600 focus:ring-blue-500"
                    :disabled="disabled"
                    @change="syncToServer"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <button
      v-if="!review.isReviewReadonly.value"
      type="button"
      class="flex items-center gap-1.5 px-4 py-3 text-sm font-medium text-blue-700 hover:text-blue-900 disabled:opacity-50 transition-colors"
      :disabled="disabled"
      @click="addAlternative"
    >
      <PlusIcon class="w-4 h-4" />
      <span>Add Alternative</span>
    </button>

    <div v-if="localAlternatives.length > 0" class="px-4 py-3">
      <div class="flex items-center gap-3">
        <label class="text-sm font-medium text-gray-700">Overall Assessment</label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="localEquipoise"
            type="checkbox"
            :disabled="disabled"
            class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            @change="syncToServer"
          />
          <span class="text-sm text-gray-600"
            >Equipoise (alternatives are considered equivalent)</span
          >
        </label>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useDebounceFn } from "@vueuse/core";
import type {
  MedicalAlternative,
  MedicalAlternativesValue,
} from "@core/domain/types/attributeValue.type";
import {
  PositionInTherapy,
  BenchmarkAuthority,
  ModeOfUse,
  InnovationStatus,
} from "@core/domain/types/attributeValue.type";
import type { ReviewItem } from "@core/domain/models/reviewItem.model";
import type { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import TextInput from "@app/components/Global/Inputs/TextInput.vue";
import { XMarkIcon, PlusIcon } from "@heroicons/vue/24/solid";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "../../../use-review";
import useSnackbar from "@app/composables/use-snackbar";
import { HttpException } from "@core/exceptions/http.exception";
import { SnackbarState } from "@app/types";
import { errorMessage } from "@app/utils/error-message";
import Combobox from "@app/components/Global/Inputs/Combobox.vue";

const positionInTherapyOptions = [
  { value: PositionInTherapy.FirstInLine, label: "First in line" },
  { value: PositionInTherapy.SecondInLine, label: "Second in line" },
  { value: PositionInTherapy.ThirdInLine, label: "Third in line" },
];

const benchmarkAuthorityOptions = [
  { value: BenchmarkAuthority.GoldStandard, label: "Gold standard" },
  { value: BenchmarkAuthority.StandardOfCare, label: "Standard of care" },
  { value: BenchmarkAuthority.NotEstablished, label: "Not established" },
];

const modeOfUseOptions = [
  { value: ModeOfUse.StandAlone, label: "Stand alone" },
  { value: ModeOfUse.Adjunctive, label: "Adjunctive" },
];

const innovationStatusOptions = [{ value: InnovationStatus.FirstInClass, label: "First in class" }];

const props = defineProps<{
  study: ReviewItem;
  attributeStructure: AttributeStructure;
  disabled?: boolean;
  initialExpandedIndex?: number;
}>();

const review = injectStrict(ReviewKey);
const snackbar = useSnackbar();
const currentAttribute = computed(() =>
  props.study.synthesis?.attributes?.find(
    (attr) => attr.attributeStructureId === props.attributeStructure?.id,
  ),
);

let tempIdCounter = 0;

function normalizeAlternative(alt: any): MedicalAlternative & { _tempId: number } {
  return {
    ...alt,
    indications: alt.indications || [],
    contraindications: alt.contraindications || [],
    targetPopulation: alt.targetPopulation || [],
    assessment: alt.assessment || {
      positionInTherapy: undefined,
      benchmarkAuthority: undefined,
      modeOfUse: undefined,
      innovationStatus: undefined,
    },
    _tempId: tempIdCounter++,
  };
}

const localAlternatives = ref<Array<MedicalAlternative & { _tempId: number }>>([]);
const localEquipoise = ref(false);
const expandedIndex = ref<number | null>(props.initialExpandedIndex ?? null);

watch(
  () => props.initialExpandedIndex,
  (val) => {
    if (val !== undefined) {
      expandedIndex.value = val;
    }
  },
);

function toggleExpand(index: number) {
  expandedIndex.value = expandedIndex.value === index ? null : index;
}

let skipServerLoadUntil = 0;
const SKIP_DURATION = 3000;

function loadFromServer() {
  const attr = currentAttribute.value;
  if (attr) {
    const value = attr.value as MedicalAlternativesValue;
    localAlternatives.value = (value?.alternatives || []).map(normalizeAlternative);
    localEquipoise.value = value?.equipoise || false;
  } else {
    localAlternatives.value = [];
    localEquipoise.value = false;
  }
}

watch(
  () => props.study.id,
  () => {
    loadFromServer();
  },
  { immediate: true },
);

watch(
  () => props.attributeStructure?.id,
  () => {
    loadFromServer();
  },
);

watch(
  () => currentAttribute.value?.value,
  () => {
    if (Date.now() < skipServerLoadUntil) {
      return;
    }
    loadFromServer();
  },
  { deep: true },
);

function addAlternative() {
  localAlternatives.value.push(
    normalizeAlternative({
      name: "",
      description: "",
      indications: [],
      contraindications: [],
      targetPopulation: [],
      assessment: {
        positionInTherapy: undefined,
        benchmarkAuthority: undefined,
        modeOfUse: undefined,
        innovationStatus: undefined,
      },
    }),
  );
  expandedIndex.value = localAlternatives.value.length - 1;
}

function addToArray(array: string[] | undefined, value: string) {
  if (!value.trim()) return;
  if (array) {
    array.push(value.trim());
  }
  syncToServer();
}

function removeFromArray(array: string[] | undefined, index: number) {
  if (array) {
    array.splice(index, 1);
  }
  syncToServer();
}

function removeAlternative(index: number) {
  localAlternatives.value.splice(index, 1);
  syncToServer();
}

function syncToServer() {
  const cleanedAlternatives = localAlternatives.value.map((alt) => {
    const { _tempId, ...rest } = alt;
    return rest;
  });

  debouncedSave({
    alternatives: cleanedAlternatives,
    equipoise: localEquipoise.value,
  });
}

const debouncedSave = useDebounceFn(async (value: MedicalAlternativesValue) => {
  try {
    skipServerLoadUntil = Date.now() + SKIP_DURATION;
    await review.saveMedicalAlternativesAttributeValue(
      props.study.id,
      props.attributeStructure.id,
      value,
    );
    skipServerLoadUntil = Date.now() + SKIP_DURATION;
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
  }
}, 1000);

function useAlternativeName(alternative: MedicalAlternative & { _tempId: number }, index: number) {
  return computed<string>(() =>
    alternative.name?.trim() ? alternative.name : `Alternative ${index + 1}`,
  );
}
</script>
